<script setup lang="ts">
import { computed, ref, onBeforeUnmount, onMounted } from 'vue'
import { usePortalOrganizationsStore } from '@/stores/portalOrganizations'

const emit = defineEmits<{ change: [organizationId: string] }>()

const store = usePortalOrganizationsStore()
const open = ref(false)
const root = ref<HTMLElement | null>(null)

const label = computed(() => store.active?.name ?? 'Seleccionar organización')

function toggle() {
  open.value = !open.value
}

function select(id: string) {
  open.value = false
  if (id === store.activeId) return
  store.setActive(id)
  emit('change', id)
}

function onDocumentClick(event: MouseEvent) {
  if (!root.value?.contains(event.target as Node)) open.value = false
}

function onKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') open.value = false
}

onMounted(() => {
  document.addEventListener('click', onDocumentClick)
  document.addEventListener('keydown', onKeydown)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocumentClick)
  document.removeEventListener('keydown', onKeydown)
})
</script>

<template>
  <div
    v-if="store.isMultiOrg"
    ref="root"
    class="relative"
    data-testid="portal-org-switcher"
  >
    <button
      type="button"
      class="flex cursor-pointer items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm
             font-medium text-white/90 hover:bg-white/10 hover:text-white"
      :aria-expanded="open"
      aria-haspopup="listbox"
      aria-label="Cambiar de organización"
      data-testid="portal-org-switcher-trigger"
      @click="toggle"
    >
      <span class="plutto-icon plutto-icon--outlined text-base">apartment</span>
      <span class="max-w-[12rem] truncate">{{ label }}</span>
      <span class="plutto-icon plutto-icon--outlined text-base">expand_more</span>
    </button>

    <ul
      v-if="open"
      role="listbox"
      data-testid="portal-org-switcher-menu"
      class="absolute right-0 z-20 mt-1 w-64 overflow-hidden rounded-xl border border-gray-200
             bg-white py-1 shadow-lg"
    >
      <li
        v-for="org in store.items"
        :key="org.id"
      >
        <button
          type="button"
          role="option"
          :aria-selected="org.id === store.activeId"
          data-testid="portal-org-option"
          class="flex w-full cursor-pointer items-center justify-between gap-3 px-4 py-2
                 text-left text-sm text-gray-700 hover:bg-gray-50"
          :class="{ 'bg-indigo-50 font-medium text-indigo-700': org.id === store.activeId }"
          @click="select(org.id)"
        >
          <span class="truncate">{{ org.name }}</span>
          <span class="shrink-0 text-xs text-gray-400">
            {{ org.contracts_count }}
          </span>
        </button>
      </li>
    </ul>
  </div>
</template>
